import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Eye, EyeOff, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fintech-notify-check/artifacts/2hk0onm3_My%20Direct%20debit%20reminder%20app%20logo.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success('Password updated!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-page flex flex-col items-center justify-center p-4">
        <p className="text-text-secondary mb-4">Invalid reset link.</p>
        <Link to="/auth" className="text-primary font-semibold hover:underline">Go to Sign In</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page flex flex-col">
      <header className="px-4 py-6">
        <div className="max-w-md mx-auto flex items-center justify-center">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="My Direct Debits" className="h-12 w-12 object-contain" />
            <div>
              <h1 className="font-heading font-bold text-dark text-xl">My Direct Debits</h1>
              <p className="text-xs text-text-light">Reminder App</p>
            </div>
          </Link>
        </div>
      </header>

      <main className="flex-1 px-4 py-8">
        <div className="max-w-md mx-auto">
          <div className="card-default p-8">
            {success ? (
              <div className="text-center" data-testid="reset-success">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                </div>
                <h2 className="font-heading font-bold text-dark text-2xl mb-2">Password Updated</h2>
                <p className="text-text-secondary mb-6">Your password has been reset successfully.</p>
                <Link to="/auth" className="btn-primary inline-block px-8 py-3 text-base" data-testid="reset-signin-link">
                  Sign In
                </Link>
              </div>
            ) : (
              <>
                <h2 className="font-heading font-bold text-dark text-2xl text-center mb-2">Set New Password</h2>
                <p className="text-text-secondary text-center mb-8 text-sm">Enter your new password below.</p>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">New Password</label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter new password"
                        data-testid="reset-password-input"
                        className="input-field w-full pr-12"
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-text-light hover:text-text-secondary"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Confirm Password</label>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Confirm new password"
                      data-testid="reset-confirm-input"
                      className="input-field w-full"
                      required
                      minLength={6}
                    />
                  </div>
                  {error && (
                    <div className="bg-alert-light text-alert text-sm p-3 rounded-lg" data-testid="reset-error">
                      {error}
                    </div>
                  )}
                  <Button type="submit" disabled={loading} className="btn-primary w-full py-3 text-base" data-testid="reset-submit-btn">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Update Password'}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
